/**
 * Auth Store — 管理登录状态
 * 对齐 account-and-membership.md
 */

import { create } from 'zustand';
import { bffClient } from '../lib/api/bff-client';

interface User {
  id: string;
  email: string;
  displayName?: string;
  tier: 'free' | 'pro';
  proUntil?: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;

  init: () => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  error: null,
  initialized: false,

  init: async () => {
    try {
      const data = await bffClient.auth.me();
      set({ user: data.user, initialized: true });
    } catch {
      set({ user: null, initialized: true });
    }
  },

  register: async (email, password, displayName) => {
    set({ loading: true, error: null });
    try {
      const data = await bffClient.auth.register(email, password, displayName);
      set({ user: data.user, loading: false });
      return true;
    } catch (e) {
      const msg = (e as Error).message;
      set({
        error: msg.includes('EMAIL_TAKEN') ? '邮箱已被注册' : msg,
        loading: false,
      });
      return false;
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const data = await bffClient.auth.login(email, password);
      set({ user: data.user, loading: false });
      return true;
    } catch (e) {
      const msg = (e as Error).message;
      set({
        error: msg.includes('INVALID_CREDENTIALS') ? '邮箱或密码错误' : msg,
        loading: false,
      });
      return false;
    }
  },

  logout: async () => {
    try {
      await bffClient.auth.logout();
    } catch {
      // 静默
    }
    set({ user: null });
  },
}));
